import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Add, AssignmentTurnedInOutlined, CheckCircleOutline, DeleteOutline, EditNoteOutlined, EditOutlined, FileDownload, UploadFileOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DataTable from "../../../components/common/tables/DataTable";
import EntityFormDialog from "../components/EntityFormDialog";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import { ACCEPTANCE_STATUSES } from "constants/projectRules";
import { calculateAcceptance } from "../../contracts/utils/commercialCalculations";
import { AppButton } from "components/common/buttons";
import { subcontractApi } from "../services/subcontractApi";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";
import { useAuth } from "context/AuthContext";
import { formatDate, formatAmount } from "utils/formatters";
import readAcceptanceItemsImportExcel from "../utils/acceptance-items/importAcceptanceItemsExcel";
import validateAcceptanceItemsImport from "../utils/acceptance-items/validateAcceptanceItemsImport";
import ApprovalTimeline from "../components/ApprovalTimeline";
import { getAllUsers } from "../../systems/services/user.service";
import exportAcceptanceReport from "../utils/subcontracts/exportAcceptanceReport";
import { ReviewNoteField } from "../tabs/ApprovalWorkflowDialogs";
import {
  compareApprovalProfiles,
  getLatestApprovalProfile as getLatestProfile,
  isApprovedStatus,
} from "../utils/subcontracts/approvalStatus";

// const money = (value) => `${new Intl.NumberFormat("vi-VN").format(value || 0)} d`;
const itemLabel = (item) => {
  const baseLabel = `${item.itemCode || ""} - ${item.itemName || ""}`.replace(/^- | - $/, "");
  return item.difficultyLevel == null ? baseLabel : `${baseLabel} (KK ${item.difficultyLevel})`;
};
const normalize = (value) => String(value || "").trim().toUpperCase();
const getId = (value, key) => value?.[key] ?? value?.[`${key[0].toUpperCase()}${key.slice(1)}`];
const getValue = (value, key) => value?.[key] ?? value?.[`${key[0].toUpperCase()}${key.slice(1)}`];
const getUserId = (user) => Number(user?.userId ?? user?.UserId);
const itemIdOf = (item) => item?.itemId ?? item?.ItemId;
const acceptanceProfileIdOf = (item) => item?.acceptanceProfileId ?? item?.AcceptanceProfileId;
const acceptanceApprovalProfileIdOf = (item) => item?.acceptanceApprovalProfileId ?? item?.AcceptanceApprovalProfileId;
const acceptanceItemIdOf = (item) => item?.acceptanceItemId ?? item?.AcceptanceItemId;
const draftAcceptanceItemIdOf = (item) => item?.drafAcceptanceItemId ?? item?.DrafAcceptanceItemId;
const draftAcceptanceLogPrefix = "[AcceptanceDraftDetail]";
const getDraftAcceptanceErrorDetails = (error) => ({
  message: error?.message,
  status: error?.response?.status,
  response: error?.response?.data,
  method: error?.config?.method,
  url: error?.config?.url,
});
const getScopeId = (value) => value?.subcontractItemScopeId ?? value?.SubcontractItemScopeId ?? value?.scopeId;
const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("vi-VN")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0111/g, "d")
    .replace(/Ä‘/g, "d");
const isPendingApprovalStatus = (value) => {
  const status = normalizeStatusKey(value);
  return (status.includes("cho") && status.includes("duyet")) || status.includes("dang xu ly");
};
const isEditingApprovalStatus = (value) => {
  const status = normalizeStatusKey(value);
  return status.includes("chinh sua") || status.includes("dang sua") || status.includes("yeu cau sua");
};
const getSubmittedUserId = (profile) => Number(getValue(profile, "submittedUserId"));
const getApprovalSteps = (profile) => getValue(profile, "steps") || [];
const getCurrentApprovalStep = (profile) => {
  const steps = getApprovalSteps(profile);
  const currentStepOrder = Number(getValue(profile, "currentStepOrder"));
  return steps.find((step) => Number(getValue(step, "stepOrder")) === currentStepOrder)
    || steps.find((step) => normalizeStatusKey(getValue(step, "status")).includes("dang xu ly"))
    || null;
};
const APPROVAL_STEPS = [
  { order: 1, field: "step1ApproverUserId", name: "Trợ lý KHTH", label: "Cấp 1 - Trợ lý KHTH" },
  { order: 2, field: "step2ApproverUserId", name: "Trưởng phòng KHTH", label: "Cấp 2 - Trưởng phòng KHTH" },
  { order: 3, field: "step3ApproverUserId", name: "Trưởng phòng Tài chính", label: "Cấp 3 - Trưởng phòng Tài chính" },
  { order: 4, field: "step4ApproverUserId", name: "Ban giám đốc", label: "Cấp 4 - Ban giám đốc" },
];
const buildApprovalStepsPayload = (form) =>
  APPROVAL_STEPS.map((step) => ({
    stepOrder: step.order,
    stepName: step.name,
    approverUserId: Number(form[step.field]),
  }));
const userLabel = (user) => {
  if (!user) return "";
  return user.fullName || user.FullName || user.userName || user.UserName || `Người dùng #${user.userId ?? user.UserId}`;
};
const getItemCodeParts = (code) =>
  String(code || "")
    .split(".")
    .map((part) => part.trim())
    .filter(Boolean);
const compareItemCodeParts = (leftPart = "", rightPart = "") => {
  const leftIsNumber = /^\d+$/.test(leftPart);
  const rightIsNumber = /^\d+$/.test(rightPart);

  if (leftIsNumber && rightIsNumber) {
    return Number(leftPart) - Number(rightPart);
  }

  return leftPart.localeCompare(rightPart, "vi", {
    numeric: true,
    sensitivity: "base",
  });
};
const compareItemCode = (left, right) => {
  const leftParts = getItemCodeParts(left?.itemCode ?? left?.ItemCode);
  const rightParts = getItemCodeParts(right?.itemCode ?? right?.ItemCode);
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const comparison = compareItemCodeParts(leftParts[index], rightParts[index]);
    if (comparison !== 0) return comparison;
  }

  return Number(itemIdOf(left) || 0) - Number(itemIdOf(right) || 0);
};
const sortItemsByCode = (items = []) => [...items].sort(compareItemCode);
const sortScopeGroups = (groups = []) => [...groups].sort((left, right) =>
  Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0)
  || String(left.label || "").localeCompare(String(right.label || ""), "vi", { sensitivity: "base" }),
);
const templateFileUrl = `${process.env.PUBLIC_URL || ""}/mau_import_nghiem_thu_quyet_dinh_giao_khoan.xlsx`;
const normalizeScopeListName = (value) => String(value || "").trim();
const isVisibleImportScopeName = (value) => {
  const name = normalizeScopeListName(value);
  return name && normalizeStatusKey(name) !== "tong hop";
};
const getAcceptanceProfileTime = (profile) => {
  const value = profile?.acceptanceDate ?? profile?.AcceptanceDate;
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};
const isBeforeAcceptanceProfile = (profile, currentProfile) => {
  const profileId = Number(acceptanceProfileIdOf(profile) || 0);
  const currentProfileId = Number(acceptanceProfileIdOf(currentProfile) || 0);
  const profileTime = getAcceptanceProfileTime(profile);
  const currentTime = getAcceptanceProfileTime(currentProfile);

  if (currentTime !== null && profileTime !== null && profileTime !== currentTime) {
    return profileTime < currentTime;
  }
  if (currentTime === null && profileTime !== null) return true;
  if (currentTime !== null && profileTime === null) return false;
  return profileId < currentProfileId;
};

const formatImportErrorValue = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleDateString("vi-VN");
  return value.toString?.() || "";
};

export default function AcceptancesTab({
  viewMode = "acceptance",
  subcontractId,
  subcontract = {},
  acceptances = [],
  items = [],
  sections = [],
  subcontractApprovalProfiles = [],
  onChanged,
}) {
  const isApprovalView = viewMode === "approval";
  const { user } = useAuth();
  const permissions = useSubcontractPermissions();
  const [dialog, setDialog] = useState(null);
  const [approvalProfiles, setApprovalProfiles] = useState([]);
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [approvalDraftItems, setApprovalDraftItems] = useState([]);
  const [approvalDraftLoading, setApprovalDraftLoading] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(null);
  // const [reviewComment, setReviewComment] = useState("");
  const reviewCommentRef = useRef("");
  const [users, setUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [exportingAcceptanceProfileId, setExportingAcceptanceProfileId] = useState(null);
  const [importDialog, setImportDialog] = useState({
    open: false,
    profile: null,
    file: null,
    fileName: "",
    result: null,
    checking: false,
    importing: false,
    error: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const canCreate = permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceCreate);
  const canUpdateAll = permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceUpdate);
  const canDelete = permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceDelete);
  const canUpdateReportedQuantity = permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceReportedQuantityUpdate);
  const canEditDetail = canUpdateAll || canUpdateReportedQuantity;
  const canSubmitAcceptanceApproval = permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceApprovalSubmit);
  const canApproveAcceptanceApproval = permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceApprovalApprove);
  const canReadAcceptanceApproval = permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceApprovalRead);
  const canExportAcceptanceReport = canReadAcceptanceApproval;
  const latestSubcontractApprovalProfile = useMemo(
    () => getLatestProfile(subcontractApprovalProfiles),
    [subcontractApprovalProfiles],
  );
  const isSubcontractApproved = isApprovedStatus(
    latestSubcontractApprovalProfile?.status ?? latestSubcontractApprovalProfile?.Status,
  );

  const loadApprovalProfiles = useCallback(async () => {
    if (!canReadAcceptanceApproval || !subcontractId) {
      setApprovalProfiles([]);
      return [];
    }

    const profiles = await subcontractApi.getAcceptanceApprovalProfilesBySubcontract(subcontractId);
    setApprovalProfiles(profiles);
    return profiles;
  }, [canReadAcceptanceApproval, subcontractId]);

  useEffect(() => {
    loadApprovalProfiles().catch(() => setApprovalProfiles([]));
  }, [loadApprovalProfiles]);

  useEffect(() => {
    getAllUsers()
      .then((result) => setUsers(result.data || []))
      .catch(() => setUsers([]));
    subcontractApi.getTeam(subcontractId)
      .then((result) => setTeamMembers(result || []))
      .catch(() => setTeamMembers([]));
  }, [subcontractId]);

  const findItem = (id) => items.find((item) => Number(item.itemId ?? item.ItemId) === Number(id));
  const scopeById = useMemo(() => {
    const map = new Map();
    sections.forEach((section) => {
      (section.scopes || []).forEach((scope) => {
        const scopeId = getScopeId(scope);
        if (!scopeId) return;
        map.set(String(scopeId), {
          ...scope,
          sectionId: getId(section, "subcontractSectionId"),
          sectionName: section.sectionName || section.name || section.SectionName,
          sectionType: section.sectionType || section.SectionType,
          sectionDisplayOrder: section.displayOrder ?? section.DisplayOrder ?? 0,
          displayOrder: scope.displayOrder ?? scope.DisplayOrder ?? 0,
        });
      });
    });
    return map;
  }, [sections]);
  const importScopeNames = useMemo(() => {
    const names = new Map();

    sections
      .flatMap((section) =>
        (section.scopes || section.Scopes || []).map((scope) => ({
          name: normalizeScopeListName(scope.scopeName || scope.ScopeName || scope.name || scope.Name),
          sectionOrder: Number(section.displayOrder ?? section.DisplayOrder ?? 0),
          scopeOrder: Number(scope.displayOrder ?? scope.DisplayOrder ?? 0),
        })),
      )
      .filter((scope) => isVisibleImportScopeName(scope.name))
      .sort((left, right) =>
        left.sectionOrder - right.sectionOrder
        || left.scopeOrder - right.scopeOrder
        || left.name.localeCompare(right.name, "vi", { sensitivity: "base" }),
      )
      .forEach((scope) => {
        const key = normalizeStatusKey(scope.name);
        if (!names.has(key)) names.set(key, scope.name);
      });

    return [...names.values()];
  }, [sections]);

  const getScopeLabel = (item) => {
    const scopeType = normalize(item.scopeType || item.ScopeType);
    const scopeName = item.scopeName || item.ScopeName;
    const provinceName = item.provinceName || item.ProvinceName;
    const communeName = item.communeName || item.CommuneName;

    if (scopeType === "PROVINCE") return provinceName || scopeName || "Tỉnh/Thành phố";
    if (scopeType === "COMMUNE") return communeName || scopeName || "Xã/Phường";
    if (scopeType === "TOTAL") return scopeName || "Tổng hợp";
    if (scopeType === "ESTIMATE") return scopeName || "Dự toán đơn";
    return scopeName || "Chưa xác định phân cấp";
  };

  const getScopeSubtitle = (item) => {
    const sectionName = item.sectionName || item.SectionName;
    const scopeType = normalize(item.scopeType || item.ScopeType);

    // if (scopeType === "COMMUNE" && provinceName) {
    //   return [sectionName, provinceName].filter(Boolean).join(" - ");
    // }
    if (scopeType === "ESTIMATE") {
      return "";
    }
    return sectionName || "";
  };

  const enrichAcceptanceItem = (acceptanceItem) => {
    const item = findItem(acceptanceItem.itemId ?? acceptanceItem.ItemId);
    const scopeId = getScopeId(acceptanceItem) || getScopeId(item);
    const scope = scopeId ? scopeById.get(String(scopeId)) : null;

    return {
      ...acceptanceItem,
      itemCode: acceptanceItem.itemCode || acceptanceItem.ItemCode || item?.itemCode || item?.ItemCode,
      itemName: acceptanceItem.itemName || acceptanceItem.ItemName || item?.itemName || item?.ItemName,
      unit: acceptanceItem.unit || acceptanceItem.Unit || item?.unit || item?.Unit,
      subcontractItemScopeId: scopeId || null,
      scopeId: scopeId || null,
      scopeName: acceptanceItem.scopeName || acceptanceItem.ScopeName || scope?.scopeName || scope?.ScopeName || scope?.name,
      scopeType: acceptanceItem.scopeType || acceptanceItem.ScopeType || scope?.scopeType || scope?.ScopeType,
      sectionId: acceptanceItem.subcontractSectionId || acceptanceItem.SubcontractSectionId || scope?.sectionId || "",
      sectionName: acceptanceItem.sectionName || acceptanceItem.SectionName || scope?.sectionName,
      sectionType: acceptanceItem.sectionType || acceptanceItem.SectionType || scope?.sectionType,
      provinceId: acceptanceItem.provinceId || acceptanceItem.ProvinceId || scope?.provinceId || scope?.ProvinceId,
      provinceName: acceptanceItem.provinceName || acceptanceItem.ProvinceName || scope?.provinceName || scope?.ProvinceName,
      communeId: acceptanceItem.communeId || acceptanceItem.CommuneId || scope?.communeId || scope?.CommuneId,
      communeName: acceptanceItem.communeName || acceptanceItem.CommuneName || scope?.communeName || scope?.CommuneName,
      sectionDisplayOrder: scope?.sectionDisplayOrder ?? 0,
      scopeDisplayOrder: scope?.displayOrder ?? 0,
    };
  };

  const groupAcceptanceItemsByScope = (acceptanceItems = []) => {
    const groups = new Map();

    sortItemsByCode(acceptanceItems.map(enrichAcceptanceItem)).forEach((item) => {
      const key = item.subcontractItemScopeId ? `scope-${item.subcontractItemScopeId}` : "scope-unknown";
      const current = groups.get(key) || {
        key,
        label: getScopeLabel(item),
        subtitle: getScopeSubtitle(item),
        displayOrder: Number(item.sectionDisplayOrder || 0) * 1000 + Number(item.scopeDisplayOrder || 0),
        items: [],
      };
      current.items.push(item);
      groups.set(key, current);
    });

    return sortScopeGroups([...groups.values()]);
  };

  const getScopeOptions = (sectionId) => {
    const section = sections.find((item) => String(getId(item, "subcontractSectionId")) === String(sectionId));
    return (section?.scopes || [])
      .filter((scope) => !(normalize(section?.sectionType) === "COMMUNE_DETAIL" && normalize(scope.scopeType) === "TOTAL"))
      .map((scope) => ({
        value: getId(scope, "subcontractItemScopeId"),
        label: scope.scopeName || scope.name || scope.ScopeName,
      }))
      .filter((item) => item.value != null);
  };
  const getItemOptions = (scopeId) => items
    .filter((item) =>
      !item.hasChildren &&
      (!scopeId || String(item.subcontractItemScopeId ?? item.SubcontractItemScopeId) === String(scopeId))
    )
    .map((item) => ({ value: item.itemId ?? item.ItemId, label: itemLabel(item) }));
  const getAcceptedBeforeQuantity = (acceptanceProfileId, itemId) => {
    if (!acceptanceProfileId || !itemId) return 0;

    const currentProfile = acceptances.find((profile) =>
      String(acceptanceProfileIdOf(profile)) === String(acceptanceProfileId),
    );

    return acceptances.reduce((total, profile) => {
      const profileId = acceptanceProfileIdOf(profile);
      if (String(profileId) === String(acceptanceProfileId)) return total;
      if (currentProfile && !isBeforeAcceptanceProfile(profile, currentProfile)) return total;

      const acceptedInProfile = (profile.items || profile.Items || []).reduce((sum, acceptanceItem) => {
        if (String(itemIdOf(acceptanceItem)) !== String(itemId)) return sum;
        return sum + Number(acceptanceItem.acceptedQuantity ?? acceptanceItem.AcceptedQuantity ?? 0);
      }, 0);

      return total + acceptedInProfile;
    }, 0);
  };

  const userOptions = useMemo(() => {
    const teamPersonnelIds = new Set(
      teamMembers
        .filter((member) => (member.isActive ?? member.IsActive) !== false)
        .map((member) => Number(member.personnelId ?? member.PersonnelId ?? member.userId ?? member.UserId))
        .filter(Number.isFinite),
    );

    return users
      .filter((user) => teamPersonnelIds.has(Number(user.personnelId ?? user.PersonnelId)))
      .map((user) => ({
        value: user.userId ?? user.UserId,
        userId: user.userId ?? user.UserId,
        personnelId: user.personnelId ?? user.PersonnelId,
        label: userLabel(user),
      }))
      .filter((item) => item.value != null);
  }, [teamMembers, users]);

  const approvalProfilesByAcceptanceId = useMemo(() => {
    const map = new Map();
    approvalProfiles.forEach((profile) => {
      const profileId = acceptanceProfileIdOf(profile);
      if (!profileId) return;
      const entries = map.get(String(profileId)) || [];
      entries.push(profile);
      map.set(String(profileId), entries);
    });

    map.forEach((entries) => entries.sort(compareApprovalProfiles));

    return map;
  }, [approvalProfiles]);

  const getLatestApprovalProfile = useCallback((acceptanceProfile) =>
    approvalProfilesByAcceptanceId.get(String(acceptanceProfileIdOf(acceptanceProfile)))?.[0] || null,
    [approvalProfilesByAcceptanceId],
  );
  const hasAcceptanceProfilePendingApproval = useMemo(
    () => [...approvalProfilesByAcceptanceId.values()].some(([profile]) =>
      isPendingApprovalStatus(profile?.status ?? profile?.Status),
    ),
    [approvalProfilesByAcceptanceId],
  );
  const isPendingApproval = (profile) => isPendingApprovalStatus(profile?.status ?? profile?.Status);
  const getAcceptanceApprovalStatus = useCallback((acceptanceProfile) => {
    const approvalProfile = getLatestApprovalProfile(acceptanceProfile);
    return approvalProfile?.status ?? approvalProfile?.Status ?? "-";
  }, [getLatestApprovalProfile]);
  const isAcceptanceProfileApproved = (acceptanceProfile) =>
    isApprovedStatus(getAcceptanceApprovalStatus(acceptanceProfile));
  const isResubmittingAcceptanceProfile = (acceptanceProfile) =>
    isEditingApprovalStatus(getAcceptanceApprovalStatus(acceptanceProfile));
  const getSubmitApprovalLabel = (acceptanceProfile) =>
    isResubmittingAcceptanceProfile(acceptanceProfile) ? "Chỉnh sửa phê duyệt" : "Gửi phê duyệt";

  const isCurrentAcceptanceApprover = (acceptanceProfile) => {
    const approvalProfile = getLatestApprovalProfile(acceptanceProfile);
    if (!approvalProfile || !getUserId(user)) return false;
    const currentStep = getCurrentApprovalStep(approvalProfile);
    const approverUserId = Number(
      getValue(currentStep, "approverUserId") || getValue(approvalProfile, "approverUserId")
    );
    return approverUserId === getUserId(user);
  };

  const canEditAcceptanceProfile = (acceptanceProfile) => {
    if (!canSubmitAcceptanceApproval || isCurrentAcceptanceApprover(acceptanceProfile)) return false;

    const approvalProfile = getLatestApprovalProfile(acceptanceProfile);
    if (!approvalProfile) return true;

    const status = approvalProfile.status ?? approvalProfile.Status;
    if (isPendingApprovalStatus(status) || isApprovedStatus(status)) return false;

    const submittedUserId = getSubmittedUserId(approvalProfile);
    return isEditingApprovalStatus(status) && submittedUserId === getUserId(user);
  };

  const canEditAcceptanceDetail = (acceptanceProfile) =>
    canEditDetail && canEditAcceptanceProfile(acceptanceProfile);

  const canSubmitAcceptanceProfile = (acceptanceProfile) =>
    canSubmitAcceptanceApproval &&
    canEditAcceptanceProfile(acceptanceProfile);

  const canApproveAcceptanceProfile = (acceptanceProfile) =>
    canApproveAcceptanceApproval &&
    isPendingApproval(getLatestApprovalProfile(acceptanceProfile)) &&
    isCurrentAcceptanceApprover(acceptanceProfile);

  const latestTimelineApprovalProfile = useMemo(() => {
    const inProgressProfiles = approvalProfiles.filter((profile) =>
      isPendingApprovalStatus(profile.status ?? profile.Status) ||
      isEditingApprovalStatus(profile.status ?? profile.Status)
    );
    return getLatestProfile(inProgressProfiles.length ? inProgressProfiles : approvalProfiles);
  }, [approvalProfiles]);

  const latestTimelineAcceptanceProfile = useMemo(
    () => acceptances.find((profile) =>
      String(acceptanceProfileIdOf(profile)) === String(acceptanceProfileIdOf(latestTimelineApprovalProfile))
    ),
    [acceptances, latestTimelineApprovalProfile],
  );

  const acceptanceTimelineLabels = useMemo(() => ({
    originTitle: "Bộ phận sản xuất",
    originSubtitle: latestTimelineAcceptanceProfile
      ? `Khởi tạo ${latestTimelineAcceptanceProfile.acceptanceName || latestTimelineAcceptanceProfile.AcceptanceName || "kỳ nghiệm thu"}`
      : "Khởi tạo kỳ nghiệm thu",
    stepActionLabel: `phê duyệt`,
    completedSubtitle: "Hoàn thành phê duyệt",
  }), [latestTimelineAcceptanceProfile]);
  const loadApprovalDraftItems = async (profile) => {
    const acceptanceProfileId = acceptanceProfileIdOf(profile);
    if (!acceptanceProfileId) {
      console.warn(`${draftAcceptanceLogPrefix} Không thể tải bản nháp vì thiếu acceptanceProfileId.`, { profile });
      return [];
    }

    const approvalProfile = getLatestApprovalProfile(profile);
    const acceptanceApprovalProfileId = acceptanceApprovalProfileIdOf(approvalProfile);
    // console.info(`${draftAcceptanceLogPrefix} Bắt đầu tải dữ liệu.`, {
    //   acceptanceProfileId,
    //   acceptanceApprovalProfileId: acceptanceApprovalProfileId || null,
    // });

    const draftItems = await (acceptanceApprovalProfileId
      ? subcontractApi.getDraftAcceptanceItemsByApprovalProfile(acceptanceApprovalProfileId)
      : subcontractApi.getDraftAcceptanceItems(acceptanceProfileId));
    const enrichedItems = draftItems.map(enrichAcceptanceItem);
    // console.info(`${draftAcceptanceLogPrefix} Đã tải dữ liệu.`, {
    //   count: enrichedItems.length,
    //   draftItemIds: enrichedItems.map(draftAcceptanceItemIdOf),
    // });
    setApprovalDraftItems(enrichedItems);
    return enrichedItems;
  };

  const openApprovalDialog = async (profile) => {
    if (!canSubmitAcceptanceProfile(profile)) return;
    setApprovalDialog(profile);
    setApprovalDraftItems([]);
    setApprovalDraftLoading(true);
    try {
      await loadApprovalDraftItems(profile);
    } catch (error) {
      console.error(`${draftAcceptanceLogPrefix} Tải dữ liệu thất bại.`, getDraftAcceptanceErrorDetails(error));
      showSnackbar(error?.response?.data?.message || "Không thể tải dữ liệu phê duyệt nghiệm thu", "error");
    } finally {
      setApprovalDraftLoading(false);
    }
  };

  const openApprovalDraftItemDialog = (draftItem) => {
    if (!approvalDialog || !canSubmitAcceptanceProfile(approvalDialog)) return;
    // console.info(`${draftAcceptanceLogPrefix} Mở dialog chỉnh sửa.`, {
    //   drafAcceptanceItemId: draftAcceptanceItemIdOf(draftItem),
    //   acceptanceProfileId: acceptanceProfileIdOf(draftItem),
    //   acceptanceApprovalProfileId: acceptanceApprovalProfileIdOf(draftItem),
    //   acceptanceItemId: acceptanceItemIdOf(draftItem),
    //   itemId: itemIdOf(draftItem),
    // });
    setDialog({
      type: "detail",
      source: "approvalDraft",
      data: enrichAcceptanceItem(draftItem),
      profile: approvalDialog,
    });
  };

  const openNewApprovalDraftItemDialog = () => {
    if (!approvalDialog || !canSubmitAcceptanceProfile(approvalDialog)) return;
    setDialog({
      type: "detail",
      source: "approvalDraft",
      profile: approvalDialog,
    });
  };

  const deleteApprovalDraftItem = async (draftItem) => {
    if (!approvalDialog || !canSubmitAcceptanceProfile(approvalDialog)) return;

    const draftItemId = draftAcceptanceItemIdOf(draftItem);
    if (!draftItemId) {
      console.error(`${draftAcceptanceLogPrefix} Không thể xóa vì thiếu ID bản nháp.`, { draftItem });
      showSnackbar("Không xác định được ID chi tiết nghiệm thu bản nháp.", "error");
      return;
    }

    // console.info(`${draftAcceptanceLogPrefix} Bắt đầu xóa.`, {
    //   draftItemId,
    //   acceptanceProfileId: acceptanceProfileIdOf(draftItem),
    //   acceptanceApprovalProfileId: acceptanceApprovalProfileIdOf(draftItem),
    //   acceptanceItemId: acceptanceItemIdOf(draftItem),
    //   itemId: itemIdOf(draftItem),
    // });

    try {
      await subcontractApi.deleteDraftAcceptanceItem(draftItemId);
      await loadApprovalDraftItems(approvalDialog);
      // console.info(`${draftAcceptanceLogPrefix} Xóa thành công.`, { draftItemId });
      showSnackbar("Xóa chi tiết nghiệm thu bản nháp thành công.");
    } catch (error) {
      console.error(`${draftAcceptanceLogPrefix} Xóa thất bại.`, {
        draftItemId,
        error: getDraftAcceptanceErrorDetails(error),
      });
      showSnackbar(
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message ||
        "Không thể xóa chi tiết nghiệm thu bản nháp.",
        "error",
      );
    }
  };

  const openImportDialog = (profile) => {
    setImportDialog({
      open: true,
      profile,
      file: null,
      fileName: "",
      result: null,
      checking: false,
      importing: false,
      error: "",
    });
  };

  const columns = useMemo(
    () => [
      { accessorKey: "acceptanceName", header: "Kỳ nghiệm thu" },
      {
        accessorKey: "acceptanceDate", header: "Ngày nghiệm thu",
        // Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString("vi-VN") : "-"
        Cell: ({ cell }) => formatDate(cell.getValue())
      },
      {
        id: "totalAmount",
        header: "Giá trị nghiệm thu",
        accessorFn: (row) => (row.items || []).reduce((sum, item) => sum + Number(item.acceptanceAmount || 0), 0),
        Cell: ({ cell }) => formatAmount(cell.getValue())
      },
      // {
      //   id: "approvalStatus",
      //   header: "Trạng thái phê duyệt",
      //   accessorFn: (row) => {
      //     return getLatestApprovalProfile(row)?.status ?? getLatestApprovalProfile(row)?.Status ?? "Chưa gửi";
      //   },
      //   Cell: ({ cell }) => <StatusChip value={cell.getValue()} />,
      // },
      ...(isApprovalView ? [{
        id: "approvalStatus",
        header: "Trạng thái phê duyệt",

        Cell: ({ row }) => {
          const profile = getLatestApprovalProfile(row.original);

          return (
            <StatusChip
              value={profile?.status ?? "Chưa gửi"}
            />
          );
        },
      },
      {
        // id: "approvalOverallComment",
        // header: "Nhận xét phê duyệt",
        // accessorFn: (row) => getAcceptanceApprovalComment(row),
        id: "approvalOverallComment",
        header: "Nhận xét phê duyệt",

        Cell: ({ row }) => {
          const profile = getLatestApprovalProfile(row.original);

          return profile?.overallComment ?? "-";
        },
      }] : []),
    ],
    [getLatestApprovalProfile, isApprovalView],
  );

  const itemColumns = [
    {
      id: "item", header: "Dự toán", accessorFn: (row) => itemLabel(findItem(row.itemId) || row),
      size: 260, minSize: 170, maxSize: 350,
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    { accessorKey: "subcontractQuantity", header: "Khối lượng giao khoán", size: 80 },
    { accessorKey: "reportedQuantity", header: "Khối lượng thực hiện", size: 80 },
    { accessorKey: "acceptedQuantity", header: "Khối lượng nghiệm thu", size: 80 },
    { accessorKey: "rejectedQuantity", header: "Khối lượng chưa đạt", size: 70 },
    { accessorKey: "remainingQuantity", header: "Khối lượng còn lại", size: 70 },
    { accessorKey: "acceptanceAmount", header: "Thành tiền", size: 120, Cell: ({ cell }) => formatAmount(cell.getValue()) },
    { accessorKey: "status", header: "Trạng thái", size: 100, Cell: ({ cell }) => <StatusChip value={cell.getValue()} /> },
  ];

  const detailIdentityColumns = [
    {
      accessorKey: "itemCode", header: "STT", size: 55, maxSize: 55,
      accessorFn: (row) => findItem(row.itemId)?.itemCode || findItem(row.itemId)?.ItemCode || row.itemCode || row.ItemCode || "",
    },
    {
      accessorKey: "itemName", header: "Dự toán", size: 260, minSize: 170, maxSize: 350,
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
      accessorFn: (row) => findItem(row.itemId)?.itemName || findItem(row.itemId)?.ItemName || row.itemName || row.ItemName || "",
    },
  ];

  const approvalDraftItemColumns = [
    ...detailIdentityColumns,
    ...itemColumns.filter((column) => column.id !== "item"),
    {
      id: "actions",
      header: "Thao tác",
      size: 100,
      Cell: ({ row }) => (
        <Box>
          <Tooltip title="Chỉnh sửa">
            <IconButton size="small" onClick={() => openApprovalDraftItemDialog(row.original)}>
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Xóa">
            <IconButton size="small" color="error" onClick={() => deleteApprovalDraftItem(row.original)}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const saveProfile = async (form) => {
    if (!dialog?.data?.acceptanceProfileId && !isSubcontractApproved) {
      showSnackbar("Chỉ được thêm kỳ nghiệm thu khi giao khoán đã được phê duyệt", "error");
      return;
    }
    if (!dialog?.data?.acceptanceProfileId && hasAcceptanceProfilePendingApproval) {
      showSnackbar("Không thể thêm kỳ nghiệm thu khi đang có kỳ nghiệm thu chờ phê duyệt", "error");
      return;
    }
    if (!dialog?.data?.acceptanceProfileId && !canSubmitAcceptanceApproval) {
      showSnackbar("Bạn không có quyền thêm kỳ nghiệm thu", "error");
      return;
    }
    if (dialog?.data?.acceptanceProfileId && !canEditAcceptanceProfile(dialog.data)) {
      showSnackbar("Bạn không có quyền chỉnh sửa kỳ nghiệm thu", "error");
      return;
    }

    const payload = { ...form, subcontractId: Number(subcontractId) };
    if (dialog?.data?.acceptanceProfileId) await subcontractApi.updateAcceptanceProfile(dialog.data.acceptanceProfileId, payload);
    else await subcontractApi.createAcceptanceProfile(payload);
    await onChanged?.();
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const submitAcceptanceApproval = async (form) => {
    if (!approvalDialog || !canSubmitAcceptanceProfile(approvalDialog)) return;
    if (approvalDraftItems.length === 0) {
      throw new Error("Vui lòng thêm ít nhất một chi tiết nghiệm thu trước khi gửi phê duyệt.");
    }
    const acceptanceProfileId = acceptanceProfileIdOf(approvalDialog);
    const existingApprovals = approvalProfilesByAcceptanceId.get(String(acceptanceProfileId)) || [];
    const steps = buildApprovalStepsPayload(form);

    setApprovalSubmitting(true);
    try {
      await subcontractApi.createAcceptanceApprovalProfile({
        acceptanceProfileId: Number(acceptanceProfileId),
        subcontractId: Number(subcontractId),
        approvalName: form.approvalName || `Phê duyệt ${approvalDialog.acceptanceName || approvalDialog.AcceptanceName || "nghiệm thu"}`,
        submittedAt: new Date().toISOString(),
        submitCount: existingApprovals.length + 1,
        approverUserId: Number(form.step1ApproverUserId),
        status: "Chờ phê duyệt",
        overallComment: form.overallComment || null,
        steps,
      });

      setApprovalDialog(null);
      await Promise.all([onChanged?.(), loadApprovalProfiles()]);
      showSnackbar("Đã gửi phê duyệt nghiệm thu");
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Không thể gửi phê duyệt nghiệm thu", "error");
      throw error;
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const openReviewDialog = async (approvalProfile) => {
    try {
      const acceptanceProfile = acceptances.find((item) => String(acceptanceProfileIdOf(item)) === String(acceptanceProfileIdOf(approvalProfile)));
      if (!canApproveAcceptanceProfile(acceptanceProfile)) {
        showSnackbar("Chưa thể phê duyệt kỳ nghiệm thu này.", "error");
        return;
      }

      const acceptanceApprovalProfileId = acceptanceApprovalProfileIdOf(approvalProfile);
      const draftItems = acceptanceApprovalProfileId
        ? await subcontractApi.getDraftAcceptanceItemsByApprovalProfile(acceptanceApprovalProfileId)
        : [];
      reviewCommentRef.current = "";
      setReviewDialog({ ...approvalProfile, items: draftItems });
      // setReviewComment("");
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Không thể tải dữ liệu phê duyệt nghiệm thu", "error");
    }
  };

  const saveAcceptanceApprovalReview = async (action) => {
    const acceptanceProfile = acceptances.find((item) => String(acceptanceProfileIdOf(item)) === String(acceptanceProfileIdOf(reviewDialog)));
    if (!reviewDialog || !canApproveAcceptanceProfile(acceptanceProfile)) return;
    const acceptanceApprovalProfileId = acceptanceApprovalProfileIdOf(reviewDialog);

    setApprovalSubmitting(true);
    try {
      await subcontractApi.reviewAcceptanceApprovalProfile(acceptanceApprovalProfileId, {
        action,
        // comment: reviewComment || null,
        comment: reviewCommentRef.current || null,
      });

      setReviewDialog(null);
      await Promise.all([onChanged?.(), loadApprovalProfiles()]);
      showSnackbar(action === "revision" ? "Yêu cầu chỉnh sửa nghiệm thu" : "Duyệt nghiệm thu");
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Không thể tải dữ liệu phê duyệt nghiệm thu", "error");
    } finally {
      setApprovalSubmitting(false);
    }
  };
  const closeImportDialog = () => {
    if (importDialog.checking || importDialog.importing) return;
    setImportDialog((current) => ({ ...current, open: false }));
  };

  const handleAcceptanceImportFile = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    setImportDialog((current) => ({
      ...current,
      file,
      fileName: file?.name || "",
      result: null,
      error: "",
    }));
  };

  const checkAcceptanceImportFile = async () => {
    if (!importDialog.file) {
      setImportDialog((current) => ({ ...current, error: "Vui lòng chọn file Excel trước khi kiểm tra." }));
      return;
    }

    setImportDialog((current) => ({ ...current, checking: true, result: null, error: "" }));
    try {
      const importData = await readAcceptanceItemsImportExcel(importDialog.file);
      const result = validateAcceptanceItemsImport({
        importData,
        sections,
        items,
        acceptances,
        acceptanceProfileId: importDialog.profile?.acceptanceProfileId,
      });

      setImportDialog((current) => ({
        ...current,
        result,
        checking: false,
        error: result.isValid ? "" : "File có lỗi. Vui lòng xem bảng chi tiết bên dưới.",
      }));
    } catch (error) {
      setImportDialog((current) => ({
        ...current,
        checking: false,
        error: error.message || "Không thể đọc file Excel.",
      }));
    }
  };

  const submitAcceptanceImport = async () => {
    if (!importDialog.result?.isValid || !importDialog.profile?.acceptanceProfileId) return;
    if (!canEditAcceptanceDetail(importDialog.profile)) {
      setImportDialog((current) => ({
        ...current,
        error: "Chưa thể import chi tiết khi kỳ nghiệm thu đang chờ phê duyệt.",
      }));
      return;
    }

    setImportDialog((current) => ({ ...current, importing: true, error: "" }));
    try {
      const acceptanceProfileId = Number(importDialog.profile.acceptanceProfileId);
      const officialItems = importDialog.profile.items || [];
      for (const item of importDialog.result.items) {
        const existingOfficial = officialItems.find((acceptanceItem) => Number(itemIdOf(acceptanceItem)) === Number(item.itemId));
        const payload = {
          acceptanceProfileId,
          itemId: Number(item.itemId),
          reportedQuantity: Number(item.reportedQuantity || 0),
          acceptedQuantity: Number(item.acceptedQuantity || 0),
          status: item.status,
          rejectReason: item.rejectReason || null,
          notes: item.notes || null,
        };

        if (existingOfficial) {
          await subcontractApi.updateAcceptanceItem(acceptanceItemIdOf(existingOfficial), payload);
        } else {
          await subcontractApi.createAcceptanceItem(payload);
        }
      }

      setImportDialog({
        open: false,
        profile: null,
        file: null,
        fileName: "",
        result: null,
        checking: false,
        importing: false,
        error: "",
      });
      await onChanged?.();
      showSnackbar("Import chi tiết nghiệm thu thành công");
    } catch (error) {
      setImportDialog((current) => ({
        ...current,
        importing: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.Message ||
          error.message ||
          "Không thể import chi tiết nghiệm thu.",
      }));
    }
  };

  const transformDetail = (form) => {
    if (!canUpdateAll && canUpdateReportedQuantity) {
      return { ...form, reportedQuantity: Number(form.reportedQuantity || 0) };
    }

    const item = findItem(form.itemId);
    const contractQuantity = Number(item?.subcontractQuantity ?? form.subcontractQuantity ?? 0);
    const acceptedBefore = getAcceptedBeforeQuantity(acceptanceProfileIdOf(dialog?.profile), form.itemId);
    const calculated = calculateAcceptance(item, form.reportedQuantity, form.acceptedQuantity);
    return {
      ...form,
      subcontractQuantity: contractQuantity,
      unitPrice: item?.unitPrice ?? form.unitPrice ?? 0,
      ...calculated,
      remainingQuantity: Math.max(
        contractQuantity - acceptedBefore - Number(form.acceptedQuantity || 0),
        0,
      ),
    };
  };

  const saveItem = async (form) => {
    const acceptanceProfileId = acceptanceProfileIdOf(dialog.profile);
    const isApprovalDraft = dialog?.source === "approvalDraft";
    const approvalProfile = getLatestApprovalProfile(dialog.profile);
    const acceptanceApprovalProfileId = acceptanceApprovalProfileIdOf(approvalProfile);

    if (isApprovalDraft) {
      const draftItemId = draftAcceptanceItemIdOf(dialog?.data);
      const acceptanceItemId = acceptanceItemIdOf(dialog?.data) || null;
      const payload = {
        acceptanceProfileId,
        acceptanceApprovalProfileId: acceptanceApprovalProfileId || null,
        acceptanceItemId,
        itemId: Number(form.itemId ?? itemIdOf(dialog?.data)),
        reportedQuantity: Number(form.reportedQuantity || 0),
        acceptedQuantity: Number(form.acceptedQuantity || 0),
        status: form.status,
        rejectReason: form.rejectReason || null,
        notes: form.notes || null,
        drafAction: acceptanceItemId ? "Update" : "Create",
        drafStatus: "Draft",
      };

      if (!canSubmitAcceptanceProfile(dialog.profile)) {
        throw new Error("Chưa thể gửi phê duyệt nghiệm thu khi kỳ nghiệm thu đang chờ phê duyệt.");
      }

      // console.info(`${draftAcceptanceLogPrefix} Bắt đầu lưu.`, { draftItemId, payload });
      try {
        const updatePayload = !canUpdateAll && canUpdateReportedQuantity
          ? { reportedQuantity: Number(form.reportedQuantity || 0) }
          : payload;
        const response = draftItemId
          ? await subcontractApi.updateDraftAcceptanceItem(draftItemId, updatePayload)
          : await subcontractApi.createDraftAcceptanceItem(payload);

        // Chỉ đọc lại dữ liệu sau khi lưu. Không initialize ở đây vì stored procedure
        // đồng bộ sẽ chép dữ liệu nghiệm thu chính đè lên thay đổi bản nháp vừa cập nhật.
        const refreshedItems = await loadApprovalDraftItems(dialog.profile);
        const refreshedItem = refreshedItems.find((item) =>
          String(draftAcceptanceItemIdOf(item)) === String(draftItemId),
        );
        // console.info(`${draftAcceptanceLogPrefix} Lưu thành công.`, {
        //   draftItemId,
        //   response,
        //   refreshedItem,
        // });
        return;
      } catch (error) {
        console.error(`${draftAcceptanceLogPrefix} Lưu thất bại.`, {
          draftItemId,
          payload,
          error: getDraftAcceptanceErrorDetails(error),
        });
        throw error;
      }
    }

    const acceptanceItemId = acceptanceItemIdOf(dialog?.data);

    if (!canEditAcceptanceDetail(dialog.profile)) {
      showSnackbar("Chưa thể chỉnh sửa chi tiết nghiệm thu khi kỳ nghiệm thu đang chờ phê duyệt.", "error");
      return;
    }

    if (!canUpdateAll && canUpdateReportedQuantity) {
      await subcontractApi.updateAcceptanceItem(acceptanceItemId, {
        reportedQuantity: Number(form.reportedQuantity || 0),
      });
      await onChanged?.();
      return;
    }

    const payload = {
      acceptanceProfileId,
      itemId: Number(form.itemId),
      reportedQuantity: Number(form.reportedQuantity || 0),
      acceptedQuantity: Number(form.acceptedQuantity || 0),
      status: form.status,
      rejectReason: form.rejectReason || null,
      notes: form.notes || null,
    };
    if (acceptanceItemId) await subcontractApi.updateAcceptanceItem(acceptanceItemId, payload);
    else await subcontractApi.createAcceptanceItem(payload);
    await onChanged?.();
  };

  // Hàm callback để cập nhật giá trị comment khi có thay đổi
  const handleReviewCommentChange = useCallback((nextValue) => {
    reviewCommentRef.current = nextValue;
  }, []);
  const handleExportAcceptanceReport = async (profile) => {
    if (!isAcceptanceProfileApproved(profile)) {
      showSnackbar("Chưa thể xuất biên bản nghiệm thu khi kỳ nghiệm thu đang chờ phê duyệt.", "error");
      return;
    }

    const acceptanceProfileId = acceptanceProfileIdOf(profile);
    setExportingAcceptanceProfileId(acceptanceProfileId);
    try {
      await exportAcceptanceReport({
        subcontract,
        acceptanceProfile: profile,
        acceptances,
        items,
        sections,
      });
      showSnackbar("Xuất biên bản nghiệm thu thành công");
    } catch (error) {
      showSnackbar(error?.message || "Không thể xuất biên bản nghiệm thu", "error");
    } finally {
      setExportingAcceptanceProfileId(null);
    }
  };

  const handleDeleteAcceptanceProfile = async (acceptanceProfileId) => {
    try {
      const acceptanceProfile = acceptances.find((item) => Number(acceptanceProfileIdOf(item)) === Number(acceptanceProfileId));
      if (acceptanceProfile && !canEditAcceptanceProfile(acceptanceProfile)) {
        showSnackbar("Chưa thể xóa kỳ nghiệm thu khi kỳ nghiệm thu đang chờ phê duyệt.", "error");
        return;
      }

      await subcontractApi.deleteAcceptanceProfile(acceptanceProfileId);
      await onChanged?.();
      showSnackbar("Xóa kỳ nghiệm thu thành công");
    } catch (error) {
      const message = error?.response?.data?.message || "Không thể xóa kỳ nghiệm thu";
      showSnackbar(message, "error");
    }
  };

  // const detailFields = canUpdateAll ? [
  //   { name: "itemId", label: "Hạng mục hợp đồng", options: itemOptions, autocomplete: true, full: true, required: true, placeholder: "Tim theo ma hoac ten hạng mục..." },
  //   { name: "subcontractQuantity", label: "Khối lượng hợp đồng", type: "number", disabled: true, required: true },
  //   { name: "reportedQuantity", label: "Khối lượng thực hiện", type: "number", inputProps: { min: 0, step: "any" }, validate: (value) => Number(value) >= 0 ? "" : "Khối lượng không được âm" },
  //   { name: "acceptedQuantity", label: "Khối lượng nghiệm thu", type: "number", inputProps: { min: 0, step: "any" }, validate: (value, form) => Number(value) > Number(form.reportedQuantity || 0) ? "Khong duoc lon hon khoi luong thực hiện" : "" },
  //   { name: "rejectedQuantity", label: "Khối lượng chưa đạt", type: "number", disabled: true },
  //   { name: "remainingQuantity", label: "Khối lượng còn lại", type: "number", disabled: true },
  //   { name: "unitPrice", label: "Đơn giá", type: "number", disabled: true, required: true },
  //   { name: "acceptanceAmount", label: "Thành tiền nghiệm thu", type: "number", disabled: true },
  //   { name: "status", label: "Trạng thái nghiệm thu", options: ACCEPTANCE_STATUSES, required: true },
  //   { name: "rejectReason", label: "Ly do khong duoc nghiệm thu", multiline: true, full: true },
  //   { name: "notes", label: "Ghi chú", multiline: true, full: true },
  // ] : [
  //   { name: "reportedQuantity", label: "Khối lượng thực hiện", type: "number", inputProps: { min: 0, step: "any" }, validate: (value) => Number(value) >= 0 ? "" : "Khối lượng không được âm" },
  // ];

  const initialSectionId = dialog?.data?.sectionId
    || getId(sections[0], "subcontractSectionId")
    || "";
  const initialScopeOptions = getScopeOptions(initialSectionId);
  const initialScopeId = dialog?.data?.scopeId || initialScopeOptions[0]?.value || "";
  const getRemainingBeforeForForm = (form) => {
    const itemId = form.itemId ?? itemIdOf(dialog?.data);
    const item = findItem(itemId);
    const contractQuantity = Number(item?.subcontractQuantity ?? form.subcontractQuantity ?? 0);
    const acceptedBefore = getAcceptedBeforeQuantity(acceptanceProfileIdOf(dialog?.profile), itemId);
    return Math.max(contractQuantity - acceptedBefore, 0);
  };
  const validateReportedQuantity = (value, form) => {
    const quantity = Number(value);
    if (!Number.isFinite(quantity) || quantity < 0) return "Khối lượng thực hiện phải là số hợp lệ và không được âm";

    const remainingBefore = getRemainingBeforeForForm(form);
    return quantity > remainingBefore
      ? `Khối lượng thực hiện không được lớn hơn khối lượng còn lại (${remainingBefore})`
      : "";
  };
  const validateAcceptedQuantity = (value, form) => {
    const quantity = Number(value);
    if (!Number.isFinite(quantity) || quantity < 0) return "Khối lượng nghiệm thu phải là số hợp lệ và không được âm";
    if (quantity > Number(form.reportedQuantity || 0)) return "Không được lớn hơn khối lượng thực hiện";

    const remainingBefore = getRemainingBeforeForForm(form);
    return quantity > remainingBefore
      ? `Khối lượng nghiệm thu không được lớn hơn khối lượng còn lại (${remainingBefore})`
      : "";
  };
  const detailFields = canUpdateAll ? (form) => [
    { name: "scopeId", label: "Phân cấp", options: getScopeOptions(form.sectionId || initialSectionId), full: true, required: true },
    { name: "itemId", label: "Dự toán", options: getItemOptions(form.scopeId || initialScopeId), autocomplete: true, full: true, required: true, placeholder: "Tìm theo mã hoặc tên dự toán..." },
    { name: "subcontractQuantity", label: "Khối lượng giao khoán", type: "number", disabled: true, required: true },
    { name: "reportedQuantity", label: "Khối lượng thực hiện", type: "number", required: true, inputProps: { min: 0, step: "any" }, validate: validateReportedQuantity },
    { name: "acceptedQuantity", label: "Khối lượng nghiệm thu", type: "number", required: true, inputProps: { min: 0, step: "any" }, validate: validateAcceptedQuantity },
    { name: "rejectedQuantity", label: "Khối lượng chưa đạt", type: "number", disabled: true },
    { name: "remainingQuantity", label: "Khối lượng còn lại", type: "number", disabled: true },
    { name: "unitPrice", label: "Đơn giá", type: "number", disabled: true, required: true },
    { name: "acceptanceAmount", label: "Thành tiền nghiệm thu", type: "number", disabled: true },
    { name: "status", label: "Trạng thái nghiệm thu", options: ACCEPTANCE_STATUSES, required: true },
    { name: "rejectReason", label: "Lý do không được nghiệm thu", multiline: true, full: true },
    { name: "notes", label: "Ghi chú", multiline: true, full: true },
  ] : [
    { name: "reportedQuantity", label: "Khối lượng thực hiện", type: "number", required: true, inputProps: { min: 0, step: "any" }, validate: validateReportedQuantity },
  ];

  const approvalFields = [
    { name: "approvalName", label: "Tiêu đề phê duyệt", full: true },
    ...APPROVAL_STEPS.map((step) => ({
      name: step.field,
      label: step.label,
      options: userOptions,
      autocomplete: true,
      required: true,
      validate: step.order === APPROVAL_STEPS.length
        ? (value, form) => {
          const approverIds = APPROVAL_STEPS.map((item) => Number(item.field === step.field ? value : form[item.field]));
          return new Set(approverIds).size === approverIds.length ? "" : "Vui lòng chọn 4 người phê duyệt khác nhau.";
        }
        : undefined,
    })),
    { name: "overallComment", label: "Nội dung", multiline: true, minRows: 3, full: true },
  ];

  const approvalInitialData = approvalDialog ? (() => {
    const approvalProfile = getLatestApprovalProfile(approvalDialog);
    const steps = getApprovalSteps(approvalProfile);
    const initialData = {
      approvalName:
        approvalProfile?.approvalName ||
        approvalProfile?.ApprovalName ||
        `Phê duyệt ${approvalDialog.acceptanceName || approvalDialog.AcceptanceName || "nghiệm thu"}`,
      overallComment: approvalProfile?.overallComment || approvalProfile?.OverallComment || "",
    };

    APPROVAL_STEPS.forEach((step) => {
      const approvalStep = steps.find((item) => Number(getValue(item, "stepOrder")) === step.order);
      if (approvalStep) initialData[step.field] = getValue(approvalStep, "approverUserId");
    });

    return initialData;
  })() : {};
  const approvalDialogIsResubmit = Boolean(approvalDialog && isResubmittingAcceptanceProfile(approvalDialog));
  const resolvedApprovalFields = approvalDialogIsResubmit ? [] : approvalFields;
  const addAcceptanceProfileDisabled = !isSubcontractApproved || hasAcceptanceProfilePendingApproval;
  const addAcceptanceProfileTooltip = !isSubcontractApproved
    ? "Chưa thể thêm kỳ nghiệm thu khi giao khoán chưa được phê duyệt"
    : hasAcceptanceProfilePendingApproval
      ? "Chưa thể thêm kỳ nghiệm thu khi đang có kỳ nghiệm thu chờ phê duyệt"
      : "";

  return (
    <>
      <SectionCard
        title={isApprovalView ? "Danh sách phê duyệt nghiệm thu" : "Danh sách kỳ nghiệm thu"}
        action={!isApprovalView && canCreate && canSubmitAcceptanceApproval ? (
          <Tooltip title={addAcceptanceProfileTooltip}>
            <span>
              <AppButton
                variant="contained"
                startIcon={<Add />}
                disabled={addAcceptanceProfileDisabled}
                onClick={() => setDialog({ type: "profile" })}
              >
                Thêm kỳ nghiệm thu
              </AppButton>
            </span>
          </Tooltip>
        ) : null}
      >
        {isApprovalView && latestTimelineApprovalProfile && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Typography fontWeight={750}>
                Phê duyệt nghiệm thu
              </Typography>
              {latestTimelineAcceptanceProfile && (
                <Typography variant="body2" color="text.secondary">
                  {latestTimelineAcceptanceProfile.acceptanceName || latestTimelineAcceptanceProfile.AcceptanceName}
                </Typography>
              )}
              <StatusChip value={latestTimelineApprovalProfile.status ?? latestTimelineApprovalProfile.Status} />
            </Stack>
            <ApprovalTimeline
              subcontract={subcontract}
              approvals={[latestTimelineApprovalProfile]}
              labels={acceptanceTimelineLabels}
            />
          </Box>
        )}
        <DataTable
          columns={columns}
          data={acceptances}
          enableExpanding
          renderDetailPanel={({ row }) => (
            <Box sx={{ p: 2, bgcolor: "#f8fafc" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <strong>Chi tiết {row.original.acceptanceName}</strong>
                    {isApprovalView && <StatusChip value={getAcceptanceApprovalStatus(row.original)} />}
                  </Box>
                  {!isApprovalView && canCreate && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="end">
                      <AppButton
                        size="small"
                        startIcon={<UploadFileOutlined />}
                        disabled={groupAcceptanceItemsByScope(row.original.items || []).length === 0 ? false : true}
                        onClick={() => openImportDialog(row.original)}>
                        Import excel
                      </AppButton>
                    </Stack>
                  )}
                  {isApprovalView && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="end">
                      {canExportAcceptanceReport && isAcceptanceProfileApproved(row.original) && (
                        <AppButton
                          size="small"
                          startIcon={<FileDownload />}
                          disabled={exportingAcceptanceProfileId === acceptanceProfileIdOf(row.original)}
                          onClick={() => handleExportAcceptanceReport(row.original)}
                        >
                          Xuất biên bản
                        </AppButton>
                      )}
                      {canSubmitAcceptanceProfile(row.original) && (
                        <AppButton
                          size="small"
                          startIcon={<AssignmentTurnedInOutlined />}
                          onClick={() => openApprovalDialog(row.original)}
                        >
                          {getSubmitApprovalLabel(row.original)}
                        </AppButton>
                      )}
                      {canApproveAcceptanceProfile(row.original) && (
                        <AppButton
                          size="small"
                          startIcon={<CheckCircleOutline />}
                          onClick={() => openReviewDialog(getLatestApprovalProfile(row.original))}
                        >
                          Thực hiện phê duyệt
                        </AppButton>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Box>
              <Divider />
              {groupAcceptanceItemsByScope(row.original.items || []).length === 0 ? (
                <Alert severity="info">Chưa có chi tiết nghiệm thu.</Alert>
              ) : groupAcceptanceItemsByScope(row.original.items || []).map((group) => (
                <Box key={group.key} sx={{ mt: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontWeight={750}>{group.label}</Typography>
                    {group.subtitle && (
                      <Typography sx={{ fontSize: '12px', fontStyle: 'italic' }} color="text.secondary">Loại: {group.subtitle}</Typography>
                    )}
                  </Box>
                  <DataTable
                    columns={[...detailIdentityColumns, ...itemColumns.filter((column) => column.id !== "item")]}
                    data={group.items}
                  />
                </Box>
              ))}
            </Box>
          )}
          enableRowActions={isApprovalView
            ? false
            : canUpdateAll || canDelete}
          renderRowActions={({ row }) => (
            <Box>
              {!isApprovalView && canUpdateAll && canEditAcceptanceProfile(row.original) && (
                <Tooltip title="Chỉnh sửa">
                  <IconButton size="small" onClick={() => setDialog({ type: "profile", data: row.original })}>
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isApprovalView && canDelete && canEditAcceptanceProfile(row.original) && (
                <Tooltip title="Xóa">
                  <IconButton size="small" color="error" onClick={async () => { await handleDeleteAcceptanceProfile(row.original.acceptanceProfileId); }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        />
      </SectionCard>
      <EntityFormDialog
        open={dialog?.type === "profile"}
        onClose={() => setDialog(null)}
        title={dialog?.data ? "Cập nhật kỳ nghiệm thu" : "Thêm kỳ nghiệm thu"}
        initialData={dialog?.data || {}}
        onSubmit={saveProfile}
        fields={[
          { name: "acceptanceName", label: "Tên kỳ nghiệm thu", full: true, required: true },
          { name: "acceptanceDate", label: "Ngày nghiệm thu", type: "date", required: true, defaultToday: true },
          { name: "notes", label: "Ghi chú", multiline: true, full: true },
        ]}
      />
      <EntityFormDialog
        open={dialog?.type === "detail"}
        onClose={() => setDialog(null)}
        title={dialog?.source === "approvalDraft" && !dialog?.data
          ? "Thêm chi tiết nghiệm thu bản nháp"
          : canUpdateAll ? "Cập nhật chi tiết nghiệm thu" : "Xem chi tiết nghiệm thu"}
        initialData={dialog?.data ? enrichAcceptanceItem(dialog.data) : { sectionId: initialSectionId, scopeId: initialScopeId, reportedQuantity: 0, acceptedQuantity: 0 }}
        transformForm={transformDetail}
        onSubmit={saveItem}
        fields={detailFields}
      />
      <EntityFormDialog
        open={Boolean(approvalDialog)}
        onClose={() => {
          setApprovalDialog(null);
          setApprovalDraftItems([]);
        }}
        title={approvalDialog ? getSubmitApprovalLabel(approvalDialog) : "Gửi phê duyệt nghiệm thu"}
        initialData={approvalInitialData}
        onSubmit={submitAcceptanceApproval}
        fields={resolvedApprovalFields}
        submitLabel={approvalDialog ? getSubmitApprovalLabel(approvalDialog) : "Gửi phê duyệt"}
        // maxWidth="lg"
        maxWidth={false}
        fullWidth
        className="subcontract-dialog"
        PaperProps={{
          sx: {
            width: "calc(100vw - 32px)",
            maxWidth: "none",
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
          },
        }}
      >
        <Box sx={{ mt: resolvedApprovalFields.length ? 1 : 0 }}>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography fontWeight={750}>Chi tiết nghiệm thu bản nháp</Typography>
              {approvalDialog && <StatusChip value={getAcceptanceApprovalStatus(approvalDialog)} />}
            </Stack>
            {approvalDialog && canUpdateAll && canSubmitAcceptanceProfile(approvalDialog) && (
              <AppButton size="small" startIcon={<Add />} onClick={openNewApprovalDraftItemDialog}>
                Thêm chi tiết
              </AppButton>
            )}
          </Stack>
          {approvalDraftLoading ? (
            <LinearProgress />
          ) : groupAcceptanceItemsByScope(approvalDraftItems).length === 0 ? (
            <Alert severity="info">Chưa có chi tiết nghiệm thu bản nháp.</Alert>
          ) : groupAcceptanceItemsByScope(approvalDraftItems).map((group) => (
            <Box key={group.key} sx={{ mt: 2 }}>
              <Box sx={{ mb: 1 }}>
                <Typography fontWeight={750}>{group.label}</Typography>
                {group.subtitle && (
                  <Typography sx={{ fontSize: "12px", fontStyle: "italic" }} color="text.secondary">Loại: {group.subtitle}</Typography>
                )}
              </Box>
              <DataTable
                columns={approvalDraftItemColumns}
                data={group.items}
                enableRowActions={false}
                enableExport={false}
              />
            </Box>
          ))}
        </Box>
      </EntityFormDialog>
      <Dialog
        open={Boolean(reviewDialog)}
        onClose={() => setReviewDialog(null)}
        // maxWidth="lg"
        // fullWidth
        // PaperProps={{ sx: { borderRadius: 1, overflow: "hidden" } }}
        maxWidth={false}
        fullWidth
        className="subcontract-dialog"
        PaperProps={{
          sx: {
            width: "calc(100vw - 32px)",
            maxWidth: "none",
            maxHeight: "calc(100vh - 32px)",
            display: "flex",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontFamily: "Inter, sans-serif", fontWeight: 700 }}>
          Phê duyệt nghiệm thu
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: "Inter, sans-serif" }}>
            {reviewDialog?.approvalName || reviewDialog?.ApprovalName || reviewDialog?.acceptanceName || reviewDialog?.AcceptanceName || ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#fbfdff" }}>
          <ApprovalTimeline subcontract={subcontract} approvals={reviewDialog ? [reviewDialog] : []} labels={acceptanceTimelineLabels} />
          <Box sx={{ my: 2 }}>
            {groupAcceptanceItemsByScope(reviewDialog?.items || []).length === 0 ? (
              <Alert severity="info">Chưa có chi tiết nghiệm thu bản nháp.</Alert>
            ) : groupAcceptanceItemsByScope(reviewDialog?.items || []).map((group) => (
              <Box key={group.key} sx={{ mt: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <Typography fontWeight={750}>{group.label}</Typography>
                  {group.subtitle && (
                    <Typography sx={{ fontSize: "12px", fontStyle: "italic" }} color="text.secondary">Loại: {group.subtitle}</Typography>
                  )}
                </Box>
                <DataTable
                  columns={[...detailIdentityColumns, ...itemColumns.filter((column) => column.id !== "item" && column.id !== "actions")]}
                  data={group.items}
                  enableRowActions={false}
                  enableExport={false}
                />
              </Box>
            ))}
          </Box>
          {/* <TextField
            label="Lý do / ghi chú"
            multiline
            minRows={3}
            fullWidth
            value={reviewComment}
            onChange={(event) => setReviewComment(event.target.value)}
            sx={{ mt: 2 }}
          /> */}
          <ReviewNoteField
            label="Lý do / ghi chú"
            resetKey={reviewDialog ? acceptanceApprovalProfileIdOf(reviewDialog) : null}
            onValueChange={handleReviewCommentChange}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #e5e7eb", backgroundColor: "#ffffff" }}>
          <AppButton color="inherit" onClick={() => setReviewDialog(null)} disabled={approvalSubmitting}>
            Hủy
          </AppButton>
          <AppButton startIcon={<EditNoteOutlined />} color="warning" onClick={() => saveAcceptanceApprovalReview("revision")} disabled={approvalSubmitting}>
            Yêu cầu chỉnh sửa
          </AppButton>
          <AppButton startIcon={<CheckCircleOutline />} variant="contained" onClick={() => saveAcceptanceApprovalReview("approve")} disabled={approvalSubmitting}>
            Duyệt
          </AppButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={importDialog.open}
        onClose={closeImportDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ pb: 1, fontFamily: "Inter, sans-serif", fontWeight: 700 }}>
          Import chi tiết nghiệm thu
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: "Inter, sans-serif" }}>
            {importDialog.profile?.acceptanceName
              ? `Kỳ nghiệm thu: ${importDialog.profile.acceptanceName}`
              : "Tải file mẫu, chọn file và kiểm tra trước khi import."}
          </Typography>
          {importScopeNames.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, fontFamily: "Inter, sans-serif", wordBreak: "break-word" }}
            >
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                Danh sách các tỉnh-xã/phân cấp:
              </Box>{" "}
              {importScopeNames.join(", ")}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: "#fbfdff" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" },
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5, backgroundColor: "#f8fafc" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 0.5 }}>
                1. Tải file mẫu
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Vui lòng đổi tên sheet trong file mẫu theo loại dự toán cần nghiệm thu.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Các sheet Tổng hợp, Danh_muc_co_dinh và Phien_ban sẽ được bỏ qua.
              </Typography>
              <AppButton
                component="a"
                href={templateFileUrl}
                download="mau_import_nghiem_thu_quyet_dinh_giao_khoan.xlsx"
                variant="outlined"
                startIcon={<FileDownload />}
                sx={{ textTransform: "none", fontFamily: "Inter, sans-serif" }}
              >
                Tải file mẫu
              </AppButton>
            </Box>

            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5, backgroundColor: "#ffffff" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 1 }}>
                2. Chọn file và kiểm tra
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <AppButton
                  component="label"
                  startIcon={<UploadFileOutlined />}
                  disabled={importDialog.checking || importDialog.importing}
                >
                  Chọn file Excel
                  <input hidden type="file" accept=".xlsx,.xls" onChange={handleAcceptanceImportFile} />
                </AppButton>
                <AppButton
                  variant="contained"
                  onClick={checkAcceptanceImportFile}
                  disabled={!importDialog.file || importDialog.checking || importDialog.importing}
                >
                  {importDialog.checking ? "Đang kiểm tra..." : "Kiểm tra dữ liệu"}
                </AppButton>
              </Box>
              <Typography
                variant="body2"
                color={importDialog.fileName ? "text.primary" : "text.secondary"}
                sx={{ mt: 1, fontFamily: "Inter, sans-serif", wordBreak: "break-word" }}
              >
                {importDialog.fileName || "Chưa chọn file"}
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            File hợp lệ mới hiển thị nút import. Hệ thống tự lấy khối lượng giao khoán, đơn giá và tự tính khối lượng chưa đạt,
            khối lượng còn lại, thành tiền nghiệm thu.
          </Alert>

          {(importDialog.checking || importDialog.importing) && <LinearProgress sx={{ mb: 2 }} />}

          {importDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importDialog.error}
            </Alert>
          )}

          {importDialog.result?.isValid && (
            <Alert severity="success" sx={{ mb: 2 }}>
              File hợp lệ, sẵn sàng import {importDialog.result.items.length} chi tiết nghiệm thu.
            </Alert>
          )}

          {importDialog.result && !importDialog.result.isValid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              File có {importDialog.result.errors.length} lỗi. Vui lòng sửa file rồi kiểm tra lại.
            </Alert>
          )}

          {importDialog.result?.errors?.length > 0 && (
            <DataTable
              columns={[
                { accessorKey: "sheetName", header: "Sheet", size: 140 },
                { accessorKey: "rowNumber", header: "Dòng", size: 70 },
                { accessorKey: "importCode", header: "STT", size: 90 },
                { accessorKey: "column", header: "Cột", size: 150 },
                {
                  accessorKey: "value",
                  header: "Giá trị hiện tại",
                  size: 180,
                  Cell: ({ cell }) => formatImportErrorValue(cell.getValue()),
                },
                { accessorKey: "message", header: "Mô tả lỗi", size: 360 },
              ]}
              data={importDialog.result.errors.map((error, index) => ({ id: index, ...error }))}
              enableRowActions={false}
              enableExport={false}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #e5e7eb", backgroundColor: "#ffffff" }}>
          <AppButton color="inherit" disabled={importDialog.checking || importDialog.importing} onClick={closeImportDialog}>
            Hủy
          </AppButton>
          {importDialog.result?.isValid && (
            <AppButton
              variant="contained"
              disabled={importDialog.checking || importDialog.importing}
              onClick={submitAcceptanceImport}
            >
              {importDialog.importing ? "Đang import..." : "Import"}
            </AppButton>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
